import { createSlice } from '@reduxjs/toolkit';
import {
  fetchWpHouse,
  fetchNotionHouse,
  fetchWpHouseDetails,
  fetchNotionHouseDetails,
} from '../thunks/house.thunks';
import { fetchCurrUser } from '../thunks/users.thunks';

const initialState = {
  user: null,
  data: [],
  originalData: [],
  sorted_data: [],
  originalSorted_data: [],
  filters: {
    location: {
      lat: '33.7488',
      lng: '-84.3877',
      radius: 1000,
    },
    placeName: '',
    price_range: {
      min: 0,
      max: 5000000,
    },
    beds_range: {
      min: 1,
      max: 20,
    },
    sleeps_range: {
      min: 0,
      max: 20,
    },
    baths_range: {
      min: 1,
      max: 20,
    },
    size_sqft_range: {
      min: 0,
      max: 100000,
    },
    homeType: [],
    unitType: '',
    propertyFeatures: [],
    rentalPropertyFeatures: [],
    checkin_date: new Date().toISOString().split('T')[0],
    checkout_date: (() => {
      const t = new Date();
      t.setDate(t.getDate() + 1);
      return t.toISOString().split('T')[0];
    })(),
  },
  searchTerm: '',
  sortBy: '',
  formId: null,
  isFilter: false,
  isLoading: false,
  isFavoriteLoading: false,
  isError: null,
};

// ---------- helpers ----------
const getPrice = (x) =>
  Number(x?.meta?.hp_price ?? x?.rate_per_night?.extracted_lowest ?? 0);

const applySort = (data, sortBy) => {
  if (!Array.isArray(data)) return [];
  switch (sortBy) {
    case 'Price (low-high)':
      return [...data].sort((a, b) => getPrice(a) - getPrice(b));
    case 'Price (high-low)':
      return [...data].sort((a, b) => getPrice(b) - getPrice(a));
    default:
      return [...data];
  }
};

const shortTermHousingRentals = createSlice({
  name: 'shortTermHousingRentals',
  initialState,
  reducers: {
    setUser(state, action) {
      state.user = action.payload;
    },
    changeLocation(state, action) {
      state.filters.location.lat = action.payload.lat;
      state.filters.location.lng = action.payload.lng;
    },
    changeAddress(state, action) {
      state.filters.placeName = action.payload;
    },
    changeRadius(state, action) {
      state.filters.location.radius = parseInt(action.payload);
    },
    changePrice(state, action) {
      state.filters.price_range.min = action.payload.min;
      state.filters.price_range.max = action.payload.max;
    },
    changeSizeSqFt(state, action) {
      state.filters.size_sqft_range.min = action.payload.min;
      state.filters.size_sqft_range.max = action.payload.max;
    },
    changeBeds(state, action) {
      state.filters.beds_range.min = action.payload.min;
      state.filters.beds_range.max = action.payload.max;
    },
    changeBaths(state, action) {
      state.filters.baths_range.min = action.payload.min;
      state.filters.baths_range.max = action.payload.max;
    },
    changeCheckinDate(state, action) {
      state.filters.checkin_date = action.payload;
    },
    changeCheckoutDate(state, action) {
      state.filters.checkout_date = action.payload;
    },
    changeUnitType(state, action) {
      state.filters.unitType = action.payload;
    },
    changeRentalPropertyFeatures(state, action) {
      state.filters.rentalPropertyFeatures = action.payload;
    },
    setLoading(state, action) {
      state.isLoading = action.payload;
    },

    // ---------- SORT ----------
    setSortBy(state, action) {
      state.sortBy = action.payload;
      state.searchTerm = '';
      const sorted = applySort(state.data, state.sortBy);
      state.sorted_data = sorted;
      state.originalSorted_data = sorted;
    },

    setSearchTerm(state, action) {
      state.searchTerm = action.payload;
    },

    setDataBySearch(state, action) {
      const arr = Array.isArray(action.payload) ? [...action.payload] : [];
      let sorted = [];

      switch (state.sortBy) {
        case 'Price (low-high)':
          sorted = arr.sort((a, b) => getPrice(a) - getPrice(b));
          break;
        case 'Price (high-low)':
          sorted = arr.sort((a, b) => getPrice(b) - getPrice(a));
          break;
        default:
          sorted = arr;
      }

      state.sorted_data = arr.length ? sorted : [];
    },

    activateFilter(state, action) {
      state.isFilter = action.payload;
    },
    resetFilter(state) {
      state.filters = [];
      state.sorted_data = [];
      state.originalSorted_data = [];
    },
    resetSortedData(state) {
      state.sorted_data = [];
      state.originalSorted_data = [];
    },
    reset(state) {
      state.data = [];
      state.originalData = [];
      state.sorted_data = [];
      state.originalSorted_data = [];
      state.carDetails = [];
      state.vendorDetails = [];
      state.user = null;
    },
  },
  extraReducers(builder) {
    // FETCH CURRENT USER
    builder.addCase(fetchCurrUser.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(fetchCurrUser.fulfilled, (state, action) => {
      state.user = action.payload;
      state.isLoading = false;
    });
    builder.addCase(fetchCurrUser.rejected, (state, action) => {
      state.isLoading = false;
      console.log(action.error);
    });

    // FETCH WP HOUSE
    builder.addCase(fetchWpHouse.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(fetchWpHouse.fulfilled, (state, action) => {
      state.isLoading = true;
      let wpData = [];
      const curPos = state.filters.location;

      // Filter results by location
      const haversineDistance = (coords1, coords2) => {
        const toRad = (x) => (x * Math.PI) / 180;
        const R = 3958.8; // miles
        const dLat = toRad(coords2.lat - coords1.lat);
        const dLng = toRad(coords2.lng - coords1.lng);
        const a =
          Math.sin(dLat / 2) * Math.sin(dLat / 2) +
          Math.cos(toRad(coords1.lat)) *
            Math.cos(toRad(coords2.lat)) *
            Math.sin(dLng / 2) *
            Math.sin(dLng / 2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        return R * c;
      };

      wpData = action.payload.data.filter((item) => {
        const carCoords = {
          lat: parseFloat(item.meta.hp_latitude),
          lng: parseFloat(item.meta.hp_longitude),
        };
        const distance = haversineDistance(curPos, carCoords);
        return distance <= curPos.radius;
      });

      if (wpData.length === 0) {
        state.isLoading = false;
        return;
      }

      // Price
      const priceMin = Number(state.filters.price_range.min);
      const priceMax = Number(state.filters.price_range.max);
      wpData = wpData.filter(
        (d) =>
          Number(d?.meta?.hp_price) >= priceMin &&
          Number(d?.meta?.hp_price) <= priceMax
      );

      // Beds
      const bedsMin = Number(state.filters.beds_range.min);
      const bedsMax = Number(state.filters.beds_range.max);
      wpData = wpData.filter(
        (d) =>
          Number(d?.meta?.hp_h_fs_beds) >= bedsMin &&
          Number(d?.meta?.hp_h_fs_beds) <= bedsMax
      );

      // Baths
      const bathsMin = Number(state.filters.baths_range.min);
      const bathsMax = Number(state.filters.baths_range.max);
      wpData = wpData.filter(
        (d) =>
          Number(d?.meta?.hp_h_fs_baths) >= bathsMin &&
          Number(d?.meta?.hp_h_fs_baths) <= bathsMax
      );

      // Size sq.ft.
      const sizeSqFtMin = Number(state.filters.size_sqft_range.min);
      const sizeSqFtMax = Number(state.filters.size_sqft_range.max);
      wpData = wpData.filter(
        (d) =>
          Number(d?.meta?.hp_size_sq_ft) >= sizeSqFtMin &&
          Number(d?.meta?.hp_size_sq_ft) <= sizeSqFtMax
      );

      // Unit Type
      if (state.filters.unitType?.length > 0) {
        wpData = wpData.filter((d) =>
          state.filters.unitType
            .toString()
            .includes(d?.hp_listing_unit_type?.[0]?.name)
        );
      }

      // Rental Property Features
      if (state.filters.rentalPropertyFeatures?.length > 0) {
        const featuresToFind = state.filters.rentalPropertyFeatures;
        wpData = wpData.filter(
          (item) =>
            item.hp_listing_h_fr_property_feature &&
            item.hp_listing_h_fr_property_feature.some((feature) =>
              featuresToFind.includes(feature.name)
            )
        );
      }

      // Final: merge + sort by current sort
      const merged = [...state.data, wpData].flat();
      const sorted = applySort(merged, state.sortBy);

      state.isLoading = false;
      state.data = merged;
      state.originalData = merged;
      state.sorted_data = sorted;
      state.originalSorted_data = sorted;
    });
    builder.addCase(fetchWpHouse.rejected, (state, action) => {
      state.isLoading = false;
      state.sorted_data = [];
      state.originalData = [];
      console.log(action.error);
    });

    // FETCH REALTY API (NOTION)
    builder.addCase(fetchNotionHouse.pending, (state) => {
      state.isLoading = true;
    });

    builder.addCase(fetchNotionHouse.fulfilled, (state, action) => {
      if (!action.payload || !action.payload.data) {
        state.isLoading = false;
        return;
      }

      const priceMin = Number(state.filters.price_range.min);
      const props = action.payload.data.properties;

      if (props) {
        // Filter by price (lower bound only—keep your original rule)
        const filterPrice = props.filter(
          (item) =>
            Number(item?.rate_per_night?.extracted_lowest ?? 0) >= priceMin
        );

        const merged = [...state.data, filterPrice].flat();
        const sorted = applySort(merged, state.sortBy);

        state.isLoading = false;
        state.data = merged;
        state.originalData = merged;
        state.sorted_data = sorted;
        state.originalSorted_data = sorted;
      } else {
        // No props — just normalize existing and re-sort
        const flat = state.data.flat();
        const sorted = applySort(flat, state.sortBy);

        state.isLoading = false;
        state.data = flat;
        state.originalData = flat;
        state.sorted_data = sorted;
        state.originalSorted_data = sorted;
      }
    });

    builder.addCase(fetchNotionHouse.rejected, (state) => {
      state.isLoading = false;
      const flat = state.data.flat();
      const sorted = applySort(flat, state.sortBy);
      state.data = flat;
      state.originalData = flat;
      state.sorted_data = sorted;
      state.originalSorted_data = sorted;
    });

    // FETCH WP HOUSE DETAILS
    builder.addCase(fetchWpHouseDetails.pending, (state) => {
      state.isLoading = true;
    });

    builder.addCase(fetchWpHouseDetails.fulfilled, (state, action) => {
      state.isLoading = false;
      state.carDetails = action.payload.data;
    });

    builder.addCase(fetchWpHouseDetails.rejected, (state) => {
      state.isLoading = false;
      state.isError = true;
    });

    // FETCH NOTION HOUSE DETAILS
    builder.addCase(fetchNotionHouseDetails.pending, (state) => {
      state.isLoading = true;
    });

    builder.addCase(fetchNotionHouseDetails.fulfilled, (state, action) => {
      state.isLoading = false;
      state.carDetails = action.payload.data;
    });

    builder.addCase(fetchNotionHouseDetails.rejected, (state) => {
      state.isLoading = false;
      state.isError = true;
    });
  },
});

export const shortTermHousingRentalsReducer = shortTermHousingRentals.reducer;
export const {
  setUser,
  changeLocation,
  changeAddress,
  changeRadius,
  changePrice,
  changeBeds,
  changeBaths,
  changeCheckinDate,
  changeCheckoutDate,
  changeUnitType,
  changeSizeSqFt,
  changeRentalPropertyFeatures,
  setLoading,
  setSortBy,
  setSearchTerm,
  setDataBySearch,
  activateFilter,
  resetFilter,
  resetSortedData,
  reset,
} = shortTermHousingRentals.actions;
